import React, {useEffect, useMemo, useState} from 'react';

import classNames from 'classnames';

import Dropdown from './../../dropdown/dropdown';
import {Icon} from '../../icon/icon';
import {isMobileDevice} from '../../helpers';
import TextField, {ITextFieldProps} from './../textfield/textfield';
import Datepicker, {IDatepickerRangeProps} from '../datepicker/datepicker';
import {DateRange} from 'react-day-picker';
import {store} from '../../../../app/src/base/root-store';
import {observer} from 'mobx-react-lite';

export interface ISelectOption {
    label: string;
    value: string;
    disabled?: boolean;
    children?: JSX.Element;
}

export type TMultipleSelectValue = string[] | null;
export type TSingleSelectValue = string | null;

export interface ISelectBaseProps {
    id: string;
    label: string;
    name: string;
    options: ISelectOption[];
    attributes?: React.SelectHTMLAttributes<HTMLSelectElement>;
    invalid?: boolean;
    disabled?: boolean;
    error?: string;
    description?: string;
    labelClassName?: string;
    inputClassName?: string;
    modifier?: string;
    className?: string;
    search?: ITextFieldProps;
    style?: 'bordered' | 'borderless';
    type?: 'multiple';
    datepicker?: ISelectDatepickerProps;
    readonlyOptions?: boolean;
}

export interface ISelectDatepickerProps {
    datepickerProps: IDatepickerRangeProps;
    onChange: (rangeVal: undefined | DateRange) => void;
    customDateDisplayValue?: string;
}

export interface ISelectSingleProps extends ISelectBaseProps {
    defaultValue?: TSingleSelectValue;
    value?: TSingleSelectValue;
    onChange?: (value: TSingleSelectValue) => void;
    type: undefined;
}

export interface ISelectMultipleProps extends ISelectBaseProps {
    defaultValue?: TMultipleSelectValue;
    value?: TMultipleSelectValue;
    onChange?: (value: TMultipleSelectValue) => void;
    type: 'multiple';
}

export type ISelectProps = ISelectSingleProps | ISelectMultipleProps;

const Select = observer((props: ISelectProps): JSX.Element => {
    const labelStore = store.labelStore;
    const [value, setValue] = useState(getValue(props.defaultValue));
    const [search, setSearch] = useState<string | undefined>(undefined);
    const [isMounted, setIsMounted] = useState<boolean>(false);
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const [range, setRange] = React.useState<undefined | DateRange>(props?.datepicker?.datepickerProps?.defaultValue);
    const [isDateSelect, setIsDateSelect] = useState(false);

    const isActuallyFocused= useMemo(() => {
        if (isDateSelect) {
            return true;
        }

        return isFocused;
    }, [isFocused, isDateSelect]);

    const className: string = classNames(
        'select',
        {
            [`select--style-${props.style}`]: props.style || 'bordered',
            [`select--type-${props.type}`]: !!props.type,
            'is-dirty': typeof props.value !== 'undefined' ? props.value : value,
            'is-disabled': props.disabled,
            'is-invalid': props.invalid,
            'is-focused': isActuallyFocused,
        },
        props.modifier,
        props.className,
    );
    const isControlled = typeof props.value !== 'undefined';
    const currentValue = getValue(isControlled ? props.value : props.defaultValue);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    useEffect(() => {
        if (currentValue.length === 1 && props.options.filter((it) => it.value === currentValue[0]).length === 0) {
            setValue([props.options[0].value]);
        }
    }, [props.options]);


    const isMulti: () => boolean = () => props.type === 'multiple';

    const handleDatepickerChange: (rangeVal: undefined | DateRange) => void = (rangeVal: undefined | DateRange) => {
        setRange(rangeVal);
        props.datepicker?.onChange(rangeVal);
        if (checkIfRangeIsSelected(rangeVal)) {
            setValue([]);
            forceDropdownClose();
        }
    };

    const handleDatepickerChangeMobile: (rangeVal: undefined | DateRange) => void = (rangeVal: undefined | DateRange) => {
        setRange(rangeVal);
        props.datepicker?.onChange(rangeVal);
        if (checkIfRangeIsSelected(rangeVal)) {
            setValue([]);
            setIsFocused(false);
        }
    };

    const checkIfRangeIsSelected: (rangeVal: undefined | DateRange) => boolean = (rangeVal: undefined | DateRange) =>
        !!(rangeVal?.to && rangeVal?.from);

    const handleChange: (event: React.FormEvent<HTMLSelectElement>) => void = (event: React.FormEvent<HTMLSelectElement>): void => {
        const nextValue = isMulti() ? Array.from(event.currentTarget.selectedOptions, (option) => option.value) : getNextValue(currentValue, event.currentTarget.value, isMulti());

        // only set local state value if input is not controlled from parent
        if (!isControlled) {
            setValue(nextValue);
        }

        if (nextValue[0] === '_custom_') {
            setIsDateSelect(true);

            return;
        } else {
            setIsDateSelect(false);
        }

        if (props.onChange) {
            if (props.type === 'multiple') {
                props.onChange(nextValue);
            } else {
                props.onChange(nextValue.length ? nextValue[0] : null);
            }
        }
    };

    const renderOptions = (): JSX.Element[] => {
        const options = [...props.options];

        if (props.datepicker) {
            options.push({
                label: props.datepicker.customDateDisplayValue ?? labelStore.label('common.filter.period.custom'),
                value: '_custom_',
            });
        }

        return options.map((option: ISelectOption) => (
            <option key={option.value} value={option.value}>
                {option.label}
            </option>
        ));
    };

    const renderInput: () => JSX.Element = () => {
        if (isMounted && !isMobileDevice()) {
            return renderCustomSelect();
        }

        return renderNativeSelect();
    };

    const handleInputChange: (event: React.FormEvent<HTMLInputElement>, newValue: string) => void = (event: React.FormEvent<HTMLInputElement>, newValue: string): void => {
        const isChecked: boolean = event.currentTarget.checked;
        const nextValue = getNextValue(currentValue, newValue, isMulti());

        setRange(undefined);

        if (!isMulti() && isChecked) {
            setIsFocused(false);
        }

        if (!isControlled) {
            setValue(nextValue);
        }

        if (props.onChange) {
            if (props.type === 'multiple') {
                props.onChange(nextValue);
            } else {
                props.onChange(nextValue.length ? nextValue[0] : null);
            }
        }
    };

    const getFilter: () => ISelectOption[] = () => {
        let options: ISelectOption[] = [];

        if (search) {
            options = props.options.filter((option: ISelectOption) => option.label.toLowerCase().includes(search.toLocaleLowerCase()));
        }

        return options;
    };

    const handleDropdownChange: (visible: boolean) => void = (visible: boolean) => {
        setIsFocused(visible);
    };

    const forceDropdownClose: () => void = () => {
        setIsDateSelect(false);
        setIsFocused(false);
    };

    const handleDatepickerOpen: () => void = () => {
        setIsDateSelect(true);
    };

    const getDropdownOptions: () => JSX.Element = () => {
        const options: ISelectOption[] = search ? getFilter() : props.options;

        return (
            <React.Fragment>
                {options.map((option: ISelectOption, i: number) => (
                    <div
                        className="select__option"
                        key={props.id + '-' + i}
                        style={{
                            pointerEvents: props.readonlyOptions ? 'none' : 'auto',
                            cursor: props.readonlyOptions ? 'default' : 'pointer',
                        }}
                    >
                        <input
                            type={isMulti() ? 'checkbox' : 'radio'}
                            className="select__option-input"
                            id={props.id + '-' + i}
                            name={props.id + '-name'}
                            value={option.value}
                            checked={currentValue.includes(option.value)}
                            disabled={option.disabled}
                            onChange={(e: React.FormEvent<HTMLInputElement>): void => handleInputChange(e, option.value)}
                        />
                        <label htmlFor={props.id + '-' + i} className="select__option-label">
                            {isMulti() &&
                                <span className="select__option-indicator">
                                    <Icon className="select__option-icon" name="check" />
                                </span>
                            }
                            <span className="select__option-text">{option.children ? option.children : option.label}</span>
                        </label>
                    </div>
                ))}
            </React.Fragment>
        );
    };

    const renderCustomSelect: () => JSX.Element = () => (
        <React.Fragment>
            <p className={labelClassName}>
                {props.label}
            </p>
            <Dropdown
                className={classNames('select__dropdown', {[`select__dropdown--type-${props.type}`]: !!props.type})}
                isOpen={isActuallyFocused}
                offset={[0, 17]}
                onVisibleChange={handleDropdownChange}
                placement="bottom"
                trigger={{
                    children: (
                        <React.Fragment>
                            {!!props.datepicker?.customDateDisplayValue && (
                                <span className="dropdown__content" >{props.datepicker.customDateDisplayValue}</span>
                            )}
                            {props.options.map((el: ISelectOption, index: number) => {
                                if (currentValue.includes(el.value)) {
                                    return <span className="select__tag" key={index}>{el.label}</span>;
                                }

                                return null;
                            })}
                            <Icon name="caret-down" className="select__icon" />
                        </React.Fragment>
                    ),
                    className: 'select__inner',
                    onClick: forceDropdownClose,
                }}
                triggerType="click"
            >
                {props.search &&
                    <TextField
                        {...props.search}
                        className="select__search"
                        iconLeft="search"
                        value={search}
                        onChange={(input: string): void => setSearch(input)}
                    />
                }
                <fieldset className="select__fieldset">
                    <legend className={labelClassName}>{props.label}</legend>
                    {getDropdownOptions()}
                </fieldset>
                {!!props.datepicker &&
                    <Datepicker
                        {...props.datepicker.datepickerProps}
                        onOpen={handleDatepickerOpen}
                        onDateChange={handleDatepickerChange}
                        value={range}
                    />
                }
            </Dropdown>
        </React.Fragment>
    );

    const renderNativeSelect: () => JSX.Element = () => (
        <React.Fragment>
            <label className={labelClassName} htmlFor={props.id}>
                {props.label}
            </label>
            <div className="select__inner">
                <select
                    {...props.attributes}
                    className={inputClassName}
                    disabled={props.disabled}
                    id={props.id}
                    name={props.name}
                    onBlur={(): void => setIsFocused(false)}
                    onChange={handleChange}
                    onClick={(): void => setIsFocused(!isFocused)}
                    value={isMulti() ? currentValue : currentValue[0]}
                    multiple={isMulti()}
                >
                    {renderOptions()}
                </select>
                <Icon name="caret-down" className="select__icon" />
            </div>
            {!!props.datepicker && isDateSelect &&
                <div className="select-native-datepicker">
                    <Datepicker
                        {...props.datepicker.datepickerProps}
                        onOpen={handleDatepickerOpen}
                        onDateChange={handleDatepickerChangeMobile}
                        value={range}
                    />
                </div>
            }
        </React.Fragment>
    );

    const inputClassName: string = classNames('select__input', props.inputClassName);
    const labelClassName: string = classNames('select__label', props.labelClassName);

    return (
        <div className={className}>
            {renderInput()}
            {props.error && (
                <div className="select__error">
                    {props.error}
                </div>
            )}
            {props.description && (
                <div className="select__description">
                    {props.description}
                </div>
            )}
        </div>
    );
});

export default Select;

const getValue = (value?: TMultipleSelectValue | TSingleSelectValue): string[] => {
    if (!value) {
        return [];
    }
    if (typeof value === 'string') {
        return [value];
    }

    return value;
};

const getNextValue = (currentValue: string[], itemValue: string, multiple: boolean = false): string[] => {

    if (multiple) {
        return currentValue.includes(itemValue) ? currentValue.filter((i) => i !== itemValue) : [...currentValue, itemValue];
    }

    return currentValue.includes(itemValue) ? currentValue.filter((i) => i !== itemValue) : [itemValue];
};
